import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ConfirmModal } from '@/components/feedback/ConfirmModal';
import { SlidePanel } from '@/components/feedback/SlidePanel';
import { toast } from '@/components/feedback/useToast';
import { RolePicker } from '@/components/forms/RolePicker';
import { normalizePhone, toPhoneInputValue } from '@/shared/utils/phone';
import { hasEmoji } from '@/shared/utils/emoji';
import { focusFirstInvalidFieldAfterRender } from '@/shared/utils/focus-first-invalid-field';

import { useAssignableRoles } from '../hooks/use-assignable-roles';
import { useInviteAdmin } from '../hooks/use-admin-mutations';
import { AdminApiError } from '../types/admin.types';
import { getAdminErrorMessage } from '../utils/error-message';
import { AdminInvitePreviewModal } from './AdminInvitePreviewModal';

// Email format: local@domain.TLD where the TLD is >=2 ASCII letters (aligns the FE to the spec email rule).
const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
// [공통] 00_입력검증표준 §2.3 — 이메일 길이 5~50자 (확정 2026-07-10).
const EMAIL_MAX = 50;
// PRD 15 §4.2: required, exactly 11 digits, '010'-prefixed (tested on digits-only input).
const PHONE_REGEX = /^010\d{8}$/;

// Send-time chrome string for the preview card meta (YYYY-MM-DD HH:mm), captured once
// when the preview opens so it doesn't tick while the modal is up.
function formatSentAt(now: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

// PRD 15 §3.2: OPERATION is checked by default on invite.
const INITIAL_FORM = {
  name: '',
  email: '',
  phone: '',
  roles: ['OPERATION'] as string[],
};

type FieldKey = 'name' | 'email' | 'phone' | 'roles';

interface AdminInvitePanelProps {
  open: boolean;
  onClose: () => void;
}

export function AdminInvitePanel({ open, onClose }: AdminInvitePanelProps) {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const assignableRoles = useAssignableRoles();
  const inviteMutation = useInviteAdmin();

  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const [isDirty, setIsDirty] = useState(false);
  const [showDiscard, setShowDiscard] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSentAt, setPreviewSentAt] = useState('');
  // SHCT-694 — scope for the "focus the first field that failed" sweep.
  const formRef = useRef<HTMLDivElement>(null);

  const reset = () => {
    setForm(INITIAL_FORM);
    setErrors({});
    setIsDirty(false);
    setShowDiscard(false);
    setPreviewOpen(false);
  };

  const update = <K extends FieldKey>(key: K, value: (typeof form)[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setIsDirty(true);
    // Clear field error as soon as user edits.
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const validators: Record<FieldKey, () => string | undefined> = {
    name: () => {
      const name = form.name.trim();
      if (name.length < 2 || name.length > 50)
        return t('adminManagement.fields.name.errorRequired');
      // SHCT-860 — NAME class: 기본·일반특수문자 허용, 이모지 불허 ([공통] 00 §4.2).
      if (hasEmoji(name)) return t('common:errors.emojiNotAllowed');
      return undefined;
    },
    email: () => {
      const email = form.email.trim();
      if (!email) return t('adminManagement.fields.email.errorRequired');
      if (!EMAIL_REGEX.test(email)) return t('adminManagement.fields.email.errorInvalid');
      // SHCT-693 — [공통] 00 §2.3 caps the address at 50 chars; only the format was
      // checked here, so a 51-char address reached the invite request. PRD 15 §8.1
      // lists no separate length message, so the format message covers this too.
      //
      // The §2.3 range is "5~50자", but the 5 half is unreachable: the reference
      // regex in that same section requires a >=2-letter TLD, so the shortest address
      // it accepts is 6 chars (a@b.co) and the regex above has already run. Enforcing
      // it here would be dead code — the doc conflict is raised on the ticket.
      if (email.length > EMAIL_MAX) return t('adminManagement.fields.email.errorInvalid');
      return undefined;
    },
    phone: () => {
      const digits = normalizePhone(form.phone);
      if (!digits) return t('adminManagement.fields.phone.errorRequired');
      if (!PHONE_REGEX.test(digits))
        return t('adminManagement.fields.phone.errorInvalid');
      return undefined;
    },
    roles: () => {
      if (form.roles.length === 0) return t('adminManagement.fields.roles.errorRequired');
      return undefined;
    },
  };

  const handleBlur = (field: FieldKey) => {
    const message = validators[field]();
    setErrors((prev) => {
      const next = { ...prev };
      if (message) next[field] = message;
      else delete next[field];
      return next;
    });
  };

  const validate = (): boolean => {
    const next: Partial<Record<FieldKey, string>> = {};
    (Object.keys(validators) as FieldKey[]).forEach((field) => {
      const message = validators[field]();
      if (message) next[field] = message;
    });
    setErrors(next);
    const valid = Object.keys(next).length === 0;
    // SHCT-694 — PRD 15 §8.1: 해당 필드로 포커스가 이동한다. Deferred a frame so the
    // inline messages this reads have been committed.
    if (!valid) focusFirstInvalidFieldAfterRender(() => formRef.current);
    return valid;
  };

  const closeAndReset = () => {
    onClose();
    // Defer reset until slide-out completes (~300ms transition) so the form
    // doesn't visibly flash to its empty state during the animation.
    window.setTimeout(reset, 300);
  };

  const handleClose = () => {
    if (inviteMutation.isPending) return;
    if (isDirty) {
      setShowDiscard(true);
      return;
    }
    closeAndReset();
  };

  const handleDiscardConfirm = () => {
    setShowDiscard(false);
    closeAndReset();
  };

  const showInviteSuccessToast = () => {
    toast.success(t('adminManagement.toasts.inviteSuccess'), {
      action: {
        label: t('adminManagement.toasts.invitePreviewAction'),
        // The real BE-rendered preview page already exists — open it instead of
        // the legacy "unavailable" stub (parity with medical-staff invite).
        onClick: () => navigate('/admin-invite-email'),
      },
    });
  };

  // The actual invite send — shared by the form's [초대 발송] button and the
  // pre-send preview modal's [✉ 초대 발송] button. On a field-level API error it
  // closes the preview so the inline errors are visible back on the form.
  const performInvite = async () => {
    if (inviteMutation.isPending) return;
    try {
      await inviteMutation.mutateAsync({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        roles: form.roles,
      });
      closeAndReset();
      showInviteSuccessToast();
    } catch (e) {
      if (e instanceof AdminApiError && e.code === 'PHONE_DUPLICATE') {
        // Anti-enumeration: a phone already used by another admin shows the single shared generic
        // message (locale-aware), inline on the phone field — never the server's Korean text.
        setPreviewOpen(false);
        setErrors((prev) => ({ ...prev, phone: t('errors.phoneRejected', { ns: 'common' }) }));
        focusFirstInvalidFieldAfterRender(() => formRef.current);
        return;
      }
      if (e instanceof AdminApiError && e.fields) {
        // Server-side field errors override our client-side ones. §8.1's focus rule
        // does not care which side produced the message (SHCT-694).
        setPreviewOpen(false);
        setErrors(e.fields as Partial<Record<FieldKey, string>>);
        focusFirstInvalidFieldAfterRender(() => formRef.current);
        return;
      }
      // PRD 15 §8.2 — a failed invite shows the defined 등록 실패 copy; a mail-server
      // failure (EMAIL_SEND_FAILED) resolves its own key inside the helper. Neither
      // path may surface the server's raw message (SHCT-685).
      toast.error(
        getAdminErrorMessage(e, t, 'adminManagement.toasts.inviteError'),
      );
    }
  };

  const handleSubmit = async () => {
    if (inviteMutation.isPending) return;
    if (!validate()) return;
    await performInvite();
  };

  // [📧 이메일 미리보기] — validate first (prototype parity), then show the
  // confirmation modal populated with the live form values.
  const handlePreview = () => {
    if (inviteMutation.isPending) return;
    if (!validate()) return;
    setPreviewSentAt(formatSentAt(new Date()));
    setPreviewOpen(true);
  };

  return (
    <>
      <SlidePanel
        open={open}
        title={t('adminManagement.invitePanel.title')}
        width={520}
        onClose={handleClose}
      >
        <div ref={formRef}>
        <div className="form-field">
          <label className="form-field__label">
            {t('adminManagement.fields.name.label')}{' '}
            <span className="required">*</span>
          </label>
          <input
            type="text"
            className={`form-field__input${errors.name ? ' form-field__input--error' : ''}`}
            placeholder={t('adminManagement.fields.name.placeholder')}
            value={form.name}
            onChange={(e) => update('name', e.target.value)}
            onBlur={() => handleBlur('name')}
          />
          {errors.name && <p className="form-field__error">{errors.name}</p>}
        </div>

        <div className="form-field">
          <label className="form-field__label">
            {t('adminManagement.fields.email.label')}{' '}
            <span className="required">*</span>
          </label>
          <input
            type="email"
            className={`form-field__input${errors.email ? ' form-field__input--error' : ''}`}
            placeholder={t('adminManagement.fields.email.placeholder')}
            value={form.email}
            onChange={(e) => update('email', e.target.value)}
            onBlur={() => handleBlur('email')}
          />
          {errors.email && <p className="form-field__error">{errors.email}</p>}
        </div>

        <div className="form-field">
          <label className="form-field__label">
            {t('adminManagement.fields.phone.label')}{' '}
            <span className="required">*</span>
          </label>
          <input
            type="tel"
            className={`form-field__input${errors.phone ? ' form-field__input--error' : ''}`}
            placeholder={t('adminManagement.fields.phone.placeholder')}
            inputMode="numeric"
            value={form.phone}
            onChange={(e) => update('phone', toPhoneInputValue(e.target.value))}
            onBlur={() => handleBlur('phone')}
          />
          {errors.phone && <p className="form-field__error">{errors.phone}</p>}
        </div>

        <div className="form-field">
          <label className="form-field__label">
            {t('adminManagement.fields.roles.label')}{' '}
            <span className="required">*</span>
          </label>
          <RolePicker
            mode="single"
            options={assignableRoles}
            value={form.roles}
            onChange={(roles) => update('roles', roles)}
            idPrefix="invite-role"
            helperText={t('adminManagement.invitePanel.helperRoles')}
            disabled={inviteMutation.isPending}
          />
          {errors.roles && <p className="form-field__error">{errors.roles}</p>}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 'var(--space-2)',
            marginTop: 'var(--space-6)',
          }}
        >
          <button
            type="button"
            className="btn btn-default btn-secondary"
            onClick={handlePreview}
            disabled={inviteMutation.isPending}
            title={t('adminManagement.invitePreviewModal.previewButton')}
          >
            {t('adminManagement.invitePreviewModal.previewButton')}
          </button>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <button
              type="button"
              className="btn btn-default btn-secondary"
              onClick={handleClose}
              disabled={inviteMutation.isPending}
            >
              {t('adminManagement.invitePanel.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-default btn-primary"
              onClick={handleSubmit}
              disabled={inviteMutation.isPending}
            >
              {inviteMutation.isPending
                ? t('adminManagement.invitePanel.submitting')
                : t('adminManagement.invitePanel.submit')}
            </button>
          </div>
        </div>
        </div>
      </SlidePanel>

      <AdminInvitePreviewModal
        open={previewOpen}
        name={form.name.trim()}
        email={form.email.trim()}
        rolesLabel={form.roles.join(', ')}
        sentAt={previewSentAt}
        submitting={inviteMutation.isPending}
        onEdit={() => setPreviewOpen(false)}
        onConfirm={performInvite}
      />

      <ConfirmModal
        open={showDiscard}
        title={t('adminManagement.invitePanel.discardTitle')}
        message={t('adminManagement.invitePanel.discardMessage')}
        confirmLabel={t('adminManagement.modals.discard.confirm')}
        cancelLabel={t('adminManagement.modals.discard.cancel')}
        confirmVariant="destructive"
        onConfirm={handleDiscardConfirm}
        onCancel={() => setShowDiscard(false)}
      />
    </>
  );
}
